import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Edit2, AlertCircle } from 'lucide-react';
import { Card } from '../BossUI';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import { retryOn500 } from '../../utils/retryOn500';

// Status badge component
const StatusBadge = ({ status }) => {
  const colors = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    suspended: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status]}`}>
      {status}
    </span>
  );
};

// Modal component for adding/editing therapist
const TherapistModal = ({ therapist, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    firstName: therapist?.firstName || '',
    lastName: therapist?.lastName || '',
    email: therapist?.email || '',
    role: therapist?.role || 'therapist',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            {therapist ? 'Edit Therapist' : 'Add New Therapist'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
                required
                disabled={therapist} // Email can't be changed for existing users
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">
                Role
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2"
                required
              >
                <option value="therapist">Therapist</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : therapist ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const ManageTherapists = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  // Fetch therapists with retry
  const { data: therapists, isLoading } = useQuery({
    queryKey: ['therapists'],
    queryFn: async () => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/therapists/list`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
          },
        })
      });
      if (!response.ok) throw new Error('Failed to fetch therapists');
      return response.json();
    },
  });

  // Add therapist mutation with retry
  const addTherapist = useMutation({
    mutationFn: async (data) => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/auth/users`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.msg || 'Failed to add therapist');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['therapists']);
      setModalOpen(false);
      setError(null);
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  // Update therapist mutation with retry
  const updateTherapist = useMutation({
    mutationFn: async (data) => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/auth/users/${data.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            firstName: data.firstName,
            lastName: data.lastName,
            role: data.role
          }),
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.msg || 'Failed to update therapist');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['therapists']);
      setModalOpen(false);
      setError(null);
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  // Update therapist status mutation with retry
  const updateStatus = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await retryOn500({
        requestFn: () => fetch(`${process.env.REACT_APP_API_URL}/therapists/status/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('wmf_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status }),
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.msg || 'Failed to update status');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['therapists']);
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const handleSubmit = (formData) => {
    if (selectedTherapist) {
      updateTherapist.mutate({
        id: selectedTherapist.id,
        ...formData,
      });
    } else {
      addTherapist.mutate(formData);
    }
  };

  const handleStatusChange = (id, status) => {
    updateStatus.mutate({ id, status });
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Manage Therapists</h2>
          <p className="mt-1 text-sm text-slate-500">Add, edit, or manage therapist accounts</p>
        </div>
        <button
          onClick={() => {
            setSelectedTherapist(null);
            setModalOpen(true);
          }}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Therapist
        </button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {therapists?.therapists.map((therapist) => (
                <tr key={therapist.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                    {therapist.firstName} {therapist.lastName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {therapist.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {therapist.role}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={therapist.status}
                      onChange={(e) => handleStatusChange(therapist.id, e.target.value)}
                      className="text-sm border border-slate-300 rounded-md p-1"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {therapist.lastLoginAt
                      ? new Date(therapist.lastLoginAt).toLocaleString('en-US', { timeZone: 'America/New_York' })
                      : <span className="text-slate-400 italic">Never</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedTherapist(therapist);
                        setModalOpen(true);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {modalOpen && (
        <TherapistModal
          therapist={selectedTherapist}
          onClose={() => {
            setModalOpen(false);
            setSelectedTherapist(null);
          }}
          onSubmit={handleSubmit}
          isLoading={addTherapist.isLoading || updateTherapist.isLoading}
        />
      )}
    </div>
  );
};

export default ManageTherapists;
